import {
    ApiResponse,
    Gateway,
    GatewayList,
    Refund,
    PaginatedResponse,
} from '../../../types';
import { api, unwrapData } from '../../../app/store/api';
import { pspType } from '../pages/tabs/psp-switching/types';
import { gatewaySettings } from '../types/gateway-settings.types';
import { Transaction } from '../types/transaction.types';

export const gatewayApi = api.injectEndpoints({
    endpoints: (builder) => ({
        gateways: builder.query<
            GatewayList,
            { page?: number; type?: string; perPage?: number }
        >({
            transformResponse: unwrapData,
            query: ({ perPage, ...params }) => ({
                url: 'workspaces',
                method: 'GET',
                params: {
                    ...params,
                    ...(perPage ? { per_page: perPage } : {}),
                },
            }),
            providesTags: ['Gateway'],
        }),

        storeGateway: builder.mutation<
            Gateway,
            Pick<Gateway, 'title' | 'website'>
        >({
            transformResponse: unwrapData,
            query: (data) => ({
                url: 'workspaces',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: (result) => (result ? [{ type: 'Gateway' }] : []),
        }),

        removeGateway: builder.mutation<void, Pick<Gateway, 'id'>>({
            transformResponse: unwrapData,
            query: ({ id }) => ({
                url: `workspaces/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Gateway'],
        }),

        refundRequest: builder.mutation<Refund, Refund & { workspaceId: number }>({
            transformResponse: unwrapData,
            query: ({ workspaceId, transaction_id, ...body }) => ({
                url: `workspaces/${workspaceId}/transactions/${transaction_id}/refunds`,
                method: 'POST',
                body,
            }),
        }),

        getPspSetting: builder.query<pspType[], number>({
            transformResponse: unwrapData,
            query: (workspaceId) => ({
                url: `workspaces/${workspaceId}/settings/psp`,
                method: 'GET',
            }),
        }),

        updatePspSetting: builder.mutation<
            pspType[],
            { workspaceId: number; psp: string[] }
        >({
            transformResponse: unwrapData,
            query: ({ workspaceId, ...body }) => ({
                url: `workspaces/${workspaceId}/settings/psp`,
                method: 'PUT',
                body,
            }),
        }),

        getOverallSetting: builder.query<Partial<Gateway> & Pick<Gateway, 'id'>, number>({
            transformResponse: unwrapData,
            query: (workspaceId) => ({
                url: `workspaces/${workspaceId}/settings`,
                method: 'GET',
                cache: 'no-store',
            }),
            providesTags: (_result, _error, workspaceId) => [
                { type: 'Gateway', id: workspaceId },
            ],
        }),

        updateOverallSetting: builder.mutation<null, gatewaySettings & { workspaceId: number }>({
            transformResponse: unwrapData,
            query: ({ workspaceId, ...body }) => ({
                url: `workspaces/${workspaceId}/settings`,
                method: 'PATCH',
                body,
            }),
            invalidatesTags: (_result, _error, { workspaceId }) => [
                'Gateway',
                { type: 'Gateway', id: workspaceId },
            ],
        }),

        gatewayChart: builder.query<
            any,
            { gateway: number; period: ChartPeriod }
        >({
            transformResponse: unwrapData,
            query: ({ gateway, period }) => {
                const params =
                    typeof period === 'string'
                        ? { period }
                        : { start_date: period[0], end_date: period[1] };

                return {
                    url: `workspaces/${gateway}/charts`,
                    method: 'GET',
                    params,
                };
            },
        }),

        transactionChart: builder.query<
            any,
            { gateway: number; period: ChartPeriod }
        >({
            transformResponse: unwrapData,
            query: ({ gateway, period }) => {
                const params =
                    typeof period === 'string'
                        ? { gateway, period }
                        : { start_date: period[0], end_date: period[1] };

                return {
                    url: `workspaces/${gateway}/transactions/charts`,
                    method: 'GET',
                    params,
                };
            },
        }),

        updateTelegram: builder.mutation<Record<string, unknown>, { workspaceId: number; telegram: string }>({
            transformResponse: unwrapData,
            query: ({ workspaceId, ...body }) => ({
                url: `workspaces/${workspaceId}/settings/notifications/telegram`,
                method: 'POST',
                body,
            }),
            invalidatesTags: (result) => (result ? [{ type: 'Gateway' }] : []),
        }),


        getServiceContract: builder.query<
            { html: string; contract_number: string; filename: string },
            number
        >({
            transformResponse: unwrapData,
            query: (workspaceId) => ({
                url: `workspaces/${workspaceId}/settings/contract`,
                method: 'GET',
            }),
        }),

        getTransaction: builder.query<Transaction, { workspaceId: number; id: string }>({
            transformResponse: unwrapData,
            query: ({ workspaceId, id }) => ({
                url: `workspaces/${workspaceId}/transactions/${id}`,
                method: 'GET',
            }),
        }),

        getTransactionList: builder.query<
            PaginatedResponse<Transaction>,
            {
                workspaceId: number;
                page?: number;
                perPage?: number;
                search?: any;
            }
        >({
            transformResponse: unwrapData,
            query: ({ workspaceId, page, perPage = 15, search }) => {
                return {
                    url: `workspaces/${workspaceId}/transactions`,
                    method: 'GET',
                    params: {
                        ...(perPage && { per_page: perPage }),
                        ...(page && { page }),
                        ...search,
                    },
                };
            },
        }),

        exportTransactions: builder.mutation<
            Blob,
            {
                workspaceId: number;
                page: number;
                perPage: number;
                search?: any;
            }
        >({
            query: ({ workspaceId, page, perPage, search }) => ({
                url: `workspaces/${workspaceId}/transactions/export`,
                method: 'GET',
                params: {
                    page,
                    per_page: perPage,
                    ...search,
                },
                responseHandler: (response) => response.blob(),
            }),
        }),

    }),
});

export const {
    useStoreGatewayMutation,
    useGatewaysQuery,
    useGetPspSettingQuery,
    useUpdatePspSettingMutation,
    useGetOverallSettingQuery,
    useUpdateOverallSettingMutation,
    useRefundRequestMutation,
    useUpdateTelegramMutation,
    useGetServiceContractQuery,
    useRemoveGatewayMutation,
    useGetTransactionQuery,
    useGetTransactionListQuery,
    useExportTransactionsMutation,
    useGatewayChartQuery,
    useTransactionChartQuery,
} = gatewayApi;

type ChartPeriod =
    | 'daily'
    | 'weekly'
    | 'monthly'
    | 'threeMonths'
    | 'sixMonths'
    | 'yearly'
    | 'custom'
    | string[];
